import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  HostListener,
  ElementRef,
  ViewEncapsulation,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

/**
 * inputs:
 * -------
 * input label?: string
 * input placeholder?: string
 * input mandatory?: boolean
 * input formName?: string
 * input type radio|checkbox|string
 * input isMultiselect?: boolean
 * input maxSelect?: number
 * input minSelect?: number
 * input defaultSelected?: any
 * input showSearch?: boolean
 * input noteText?: string
 * input isDisabled?: boolean
 * input setFocus?: boolean
 * input errorMessage?: string
 * input showError?: boolean
 * ---------------------------
 * events:
 * -------
 * output focused
 * output blured
 * output clicked
 * output changed
 * output validationError
 */

@Component({
  selector: 'ops-select',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './ops-select.component.html',
  styleUrls: ['./ops-select.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class OpsSelectComponent implements OnInit, OnDestroy {
  private _minSelect = 0;
  private _maxSelect = 1;

  @Input() label?: string;
  @Input() placeholder? = '';
  @Input() mandatory?: boolean = false;
  @Input() formName?: string;
  @Input() type?: 'radio' | 'checkbox' | 'string';
  @Input() trackByKey?: string;
  @Input() isMultiselect?: boolean = false;
  @Input() set maxSelect(val: number) {
    this._maxSelect = val;

    if (this._maxSelect > 1) {
      this.isMultiselect = true;
      this.type = 'checkbox';
    } else if (!this.type && this.isMultiselect) {
      this.type = 'checkbox';
    } else if (!this.type && !this.isMultiselect) {
      this.type = 'radio';
    } else if (!this.type) {
      this.type = 'string';
    }
  }
  get maxSelect() {
    return this._maxSelect;
  }

  get listItemType() {
    if (this.type) return this.type;

    if (this.isMultiselect || this.maxSelect > 1) {
      return 'checkbox';
    }
    return 'radio';
  }

  @Input() set minSelect(val: number) {
    this._minSelect = val;

    if (this._minSelect > 1) {
      this.isMultiselect = true;
      this.type = 'checkbox';
    } else if (!this.type && this.isMultiselect) {
      this.type = 'checkbox';
    } else if (!this.type && !this.isMultiselect) {
      this.type = 'radio';
    } else if (!this.type) {
      this.type = 'string';
    }
  }
  get minSelect() {
    return this._minSelect;
  }

  private _defaultSelected: any = [];

  private _isSingleOption = false;

  @Input()
  set defaultSelected(value: any) {
    // If the input value is provided, use it; otherwise, use the default value (an empty array)

    if (!!value !== true) {
      this._defaultSelected = this.isMultiselect ? [] : '';
    } else if (typeof value === 'number' || typeof value === 'string') {
      this._defaultSelected = this.isMultiselect ? [value + ''] : value + '';
    } else if (typeof value === 'object') {
      this._defaultSelected = this.isMultiselect ? value : value[0];
    }

    this.searchQuery = '';

    if (!this.isMultiselect) {
      this.selectedValue = this._defaultSelected + '';
      this.searchQuery = this._defaultSelected + '';
    }

    this.mappedOptions = this.mapOptions();
    this.selectedItems = this.mapDefaultSelected();

    this.updateSelections();

    if (this.selectedItems.length) {
      this.highlightedIndex = this.filteredItems.indexOf(this.selectedItems[0]);
    }
  }

  get defaultSelected() {
    return this._defaultSelected.map((ds) => ds.value);
  }

  private _isPreview = false;

  @Input() set isPreview(val: boolean) {
    this._isPreview = val;

    if (!this.isMultiselect) {
      this.selectedValue = this._defaultSelected + '';
      this.searchQuery = this._defaultSelected + '';
    } else {
      this.selectedValue = '';
      this.searchQuery = '';
    }
  }

  get isPreview() {
    return this._isPreview;
  }

  set isSingleOption(val: boolean) {
    this._isSingleOption = val;
  }
  get isSingleOption() {
    return this._isSingleOption;
  }

  @Input() options: (
    | string
    | { key: any; value: string; isDisabled?: boolean }
  )[] = [];
  @Input() showSearch?: boolean = false;
  @Input() showLimitNote?: boolean;
  @Input() noteText?: string;
  @Input() isDisabled?: boolean = false;
  @Input() setFocus?: boolean = false;
  @Input() errorMessage?: string;
  @Input() showError?: boolean = false;
  @Input() allowCreate?: boolean = false;

  @Input() rotateDropdownDirection?: boolean = false;

  @Output() focused = new EventEmitter<void>();
  @Output() blured = new EventEmitter<void>();
  @Output() clicked = new EventEmitter<void>();
  @Output() changed = new EventEmitter<any[]>();
  @Output() validationError = new EventEmitter<string>();

  selectedItems: any[] = [];
  selectedValue?: string;
  searchQuery = '';
  mappedOptions: { key: any; value: string; isDisabled?: boolean }[] = [];
  isDropdownOpen = false;
  highlightedIndex: number | null = null;
  closeTimeout: any = null;

  mouseLeft = false;

  prevElement?: any;

  constructor(private elementRef: ElementRef) {}

  ngOnDestroy(): void {
    this.isDropdownOpen = false;
    clearTimeout(this.closeTimeout);
  }

  ngOnInit() {
    this.validateInputs();
    this.mappedOptions = this.mapOptions();
    this.selectedItems = this.mapDefaultSelected();
  }

  ddIconClick(event) {
    // if (!this.isDropdownOpen) this.isDropdownOpen = true;

    if (!this.isDisabled) {
      clearTimeout(this.closeTimeout);
      this.isDropdownOpen = true;
      this.clicked.emit();
      this.elementRef.nativeElement.getElementsByTagName('input')[0]?.focus();

      this.scrollToSelected();
    }
  }

  updateSelections() {
    // Clear the selectedItems list
    this.selectedItems = [];
    this.selectedValue = '';

    if (typeof this._defaultSelected !== 'object') {
      this._defaultSelected = [];
    }
    // Iterate through the _defaultSelected values and compare them with mappedOptions
    this._defaultSelected.forEach((defaultSelectedItem) => {
      const matchedOption = this.mappedOptions.find(
        (option) =>
          option.value === defaultSelectedItem.value ||
          option.key === defaultSelectedItem.key ||
          option.value === defaultSelectedItem
      );

      if (matchedOption) {
        // If a matching option is found, add it to the selectedItems list
        this.selectedItems.push(matchedOption);

        if (!this.isMultiselect) {
          this.selectedValue = matchedOption.value;
          this.searchQuery = matchedOption.value;
        }
      }
    });
  }

  // Validate inputs
  validateInputs() {
    if (!Array.isArray(this.options)) {
      throw new Error("The 'options' input must be an array.");
    }

    // Validate defaultSelected
    if (this._defaultSelected && !Array.isArray(this._defaultSelected)) {
      throw new Error("The 'defaultSelected' input must be an array.");
    }

    // if (
    //   typeof this.type !== 'string' ||
    //   !['radio', 'checkbox', 'string'].includes(this.type)
    // ) {
    //   throw new Error(
    //     "The 'type' input must be one of 'radio', 'checkbox', or 'string'."
    //   );
    // }
  }

  // Map options array to object array
  mapOptions() {
    if (this.options.length === 0) return [];

    if (
      typeof this.options[0] === 'string' ||
      typeof this.options[0] === 'number'
    ) {
      return (this.options as string[]).map((item, index) => ({
        key: index,
        value: '' + item,
      }));
    }

    return this.options as { key: any; value: string }[];
  }

  // Map default selected options array to object array
  mapDefaultSelected() {
    if (this.options.length === 1) {
      this.selectedValue = this.mappedOptions[0].value;
      this._defaultSelected = [this.mappedOptions[0].value];
      this.isSingleOption = true;
      return [this.mappedOptions[0].value];
    }

    if (
      this._defaultSelected === undefined ||
      this._defaultSelected === null ||
      this._defaultSelected === ''
    ) {
      this._defaultSelected = [];
      return [];
    }

    if (
      typeof this._defaultSelected === 'string' ||
      typeof this._defaultSelected === 'number'
    ) {
      if (this._defaultSelected === '') {
        this._defaultSelected = [];
        return [];
      } else {
        this._defaultSelected = [this._defaultSelected + ''];
        if (this.mappedOptions.length) {
          const matchedKey = (this._defaultSelected as string[]).map(
            (item, index) => {
              const keyIndex = this.mappedOptions.find(
                (mo) => mo.value === item || mo.key === item
              )?.key;
              return {
                key: keyIndex || null,
                value: item,
              };
            }
          );

          return matchedKey.filter((selItem) => selItem.key !== null);
        }
      }
    }

    if (this._defaultSelected.length === 0) return [];

    if (
      typeof this._defaultSelected === 'string' ||
      typeof this._defaultSelected === 'number'
    ) {
      return ([this._defaultSelected] as string[]).map((item, index) => {
        const keyIndex = this.mappedOptions.find(
          (mo) => mo.value === item + '' || mo.key === item + ''
        ).key;
        return {
          key: keyIndex,
          value: item,
        };
      });
    }

    if (typeof this._defaultSelected[0] === 'string') {
      const matchedItems = (this._defaultSelected as string[]).map(
        (item, index) => {
          if (this.mappedOptions.length) {
            const keyIndex = this.mappedOptions.find(
              (mo) => mo?.value === item || mo?.key === item
            )?.key;
            return {
              key: keyIndex || null,
              value: item,
            };
          }
          return {
            key: null,
            value: '',
          };
        }
      );

      return matchedItems.filter((selItem) => selItem.key !== null);
    }

    return (this._defaultSelected as { key: any; value: string }[]).map(
      (item, index) => {
        const keyIndex = this.mappedOptions.find(
          (mo) => mo.value === item.value || mo.key === item.value
        )?.key;

        return {
          key: keyIndex,
          value: item,
        };
      }
    );
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.searchQuery && this.prevElement && event.key === 'Backspace') {
      if (this.selectedValue && !this.isMultiselect && !this.searchQuery) {
        this.selectedValue = '';
        this.selectedItems = [];
        this.isDropdownOpen = true;

        this.scrollToSelected();
      } else {
        // const currentFocusedElement = document.activeElement;
        // // Get all focusable elements
        // const focusableElements = Array.from(
        //   document.querySelectorAll(
        //     'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
        //   )
        // ).filter(
        //   (el) =>
        //     !el.hasAttribute('disabled') && el.getAttribute('tabindex') !== '-1'
        // );
        // // Find the index of the currently focused element in the list of focusable elements
        // const currentIndex = focusableElements.indexOf(currentFocusedElement);
        // // Get the previous focusable element, if it exists
        // const previousElement = focusableElements[currentIndex - 1];
        // if (previousElement) {
        //   // Focus the previous element
        //   this.isDropdownOpen = false;
        //   clearTimeout(this.closeTimeout);
        //   (previousElement as any).focus();
        // }
      }
      return;
    }
  }

  onEnterPressed(event: KeyboardEvent) {
    if (this.allowCreate && this.searchQuery.length > 0) {
      const haveMatch = this.mappedOptions.find(
        (mO) => mO.value === this.searchQuery
      );
      if (!this.isMultiselect) {
        this.selectedItems = [];
      }
      if (!haveMatch) {
        if (this.options.length === 0 || typeof this.options[0] === 'string') {
          this.options.push(this.searchQuery);
        } else {
          this.options.push({
            key: this.options.length,
            value: this.searchQuery,
            isDisabled: false,
          });
        }
        this.mappedOptions.push({
          key: this.options.length,
          value: this.searchQuery,
          isDisabled: false,
        });

        if (this.selectedItems.length < this.maxSelect) {
          this.selectedItems.push({
            key: this.options.length,
            value: this.searchQuery,
          });
        }
      }

      this.emitSelectedItems();
    }
  }

  isElementFullyVisibleInContainer(
    element: HTMLElement,
    container: HTMLElement
  ) {
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const isBottomVisible =
      containerRect.bottom + container.scrollTop >
      elementRect.bottom + 200 + element.scrollHeight;

    return isBottomVisible;
  }

  get dropdownOrientation(): boolean {
    if (this.isDropdownOpen === false) {
      return false;
    }

    if (this.rotateDropdownDirection === true) {
      return this.rotateDropdownDirection;
    }

    const docContainer = document.getElementById('holding-container');
    if (docContainer) {
      const isVisible = this.isElementFullyVisibleInContainer(
        this.elementRef.nativeElement,
        docContainer
      );

      if (!isVisible) {
        return true;
      }
    }
    return false;
    // if(this.elementRef.nativeElement.offsetTop())
  }

  get isDropdownShowing(): boolean {
    return this.isDropdownOpen;
  }

  // Handle focusing in the input, open dropdown on Tab
  onFocus(event: any) {
    if (!this.prevElement) {
      this.prevElement = event?.composedPath()[0];
    }
    clearTimeout(this.closeTimeout);
    this.isDropdownOpen = true;

    this.focused.emit();

    this.scrollToSelected();
  }

  // Handle blur (close dropdown after 3 seconds unless hovering)
  onBlur() {
    if (this.isDropdownOpen) {
      if (this.mouseLeft) {
        clearTimeout(this.closeTimeout);
        this.isDropdownOpen = false;
      } else {
        this.startCloseTimeout();
      }
    }

    this.blured.emit();
  }

  // Start close timeout (3 seconds)
  startCloseTimeout(waitingTime?: number) {
    clearTimeout(this.closeTimeout);

    const wTime = waitingTime || 1000;

    if (this.isDropdownOpen) {
      this.closeTimeout = setTimeout(() => {
        this.isDropdownOpen = false;
      }, wTime);
    }
  }

  // Cancel close timeout if hovered
  @HostListener('mouseenter')
  onMouseEnter() {
    this.mouseLeft = false;
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.elementRef.nativeElement.focus();
    }
  }

  // Handle clicks to toggle dropdown
  onClick() {
    if (!this.isDisabled) {
      clearTimeout(this.closeTimeout);
      this.isDropdownOpen = true;
      this.clicked.emit();
      this.elementRef.nativeElement.focus();

      this.scrollToSelected();
    }
  }

  emitSelectedItems() {
    const selectedItemsMap = this.selectedItems.map((si) => si.value);
    if (this.isMultiselect) {
      this.changed.emit(selectedItemsMap);
    } else {
      this.changed.emit(
        selectedItemsMap?.length > 0 ? selectedItemsMap[0] : null
      );
    }
  }

  // Handle removing items from selected list
  removeSelectedItem(item: { key: any; value: string }) {
    if (!this.isDisabled && !this.isPreview) {
      this.selectedItems = this.selectedItems.filter((i) => i.key !== item.key);
      this.emitSelectedItems();
      this.elementRef.nativeElement.focus();
    }
  }

  // Handle selecting items
  onSelectItem(item: { key: any; value: string }) {
    if (this.isDisabled) return;

    if (this.isMultiselect) {
      if (this.selectedItems.filter((si) => si.key === item.key)?.length > 0) {
        this.removeSelectedItem(item);
      } else {
        if (this.maxSelect && this.selectedItems.length >= this.maxSelect) {
          this.validationError.emit(
            `You can only select up to ${this.maxSelect} items`
          );
          return;
        }
        this.selectedItems.push(item);
      }
    } else {
      this.selectedItems = [item];
      this.selectedValue = item.value;
    }
    this.emitSelectedItems();
    this.validateSelection();

    if (!this.isMultiselect && !this.showError) {
      this.isDropdownOpen = false;

      this.onBlur();

      setTimeout(
        (elRef: any) => {
          const tabEvent = new KeyboardEvent('keydown', {
            key: 'Tab',
            code: 'Tab',
            keyCode: 9, // 'Tab' key code
            which: 9, // 'Tab' key code
            bubbles: true,
            cancelable: true,
          });

          // Dispatch the event from the current element
          elRef.dispatchEvent(tabEvent);
        },
        80,
        this.elementRef.nativeElement
      );
    } else {
      this.elementRef.nativeElement.focus();
    }
  }

  onSearchChange(event: Event) {
    this.searchQuery = (event.target as HTMLInputElement).value;
    this.highlightedIndex = null; // reset highlight on search
    this.elementRef.nativeElement.focus();
  }

  validateSelection() {
    if (this.mandatory && this.selectedItems.length < this.minSelect) {
      this.showError = true;
      this.errorMessage = `Please select at least ${this.minSelect} items`;
      this.validationError.emit(this.errorMessage);
    } else {
      this.showError = false;
    }
    this.elementRef.nativeElement.focus();
  }

  get filteredItems() {
    if (this.searchQuery === this.selectedValue) {
      return this.mappedOptions;
    }
    return this.mappedOptions.filter((item) =>
      item?.value
        ?.toLowerCase()
        ?.includes(this.searchQuery?.toLowerCase() ?? '')
    );
  }

  isSelected(item: { key: any; value: string }) {
    return this.selectedItems.filter((si) => si.key === item.key)?.length > 0;
  }

  // Set styles based on type and selection state
  getItemClass(item: { key: any; value: string }) {
    return {
      selected: this.isSelected(item),
      disabled: this.isDisabled,
      highlighted: this.filteredItems.indexOf(item) === this.highlightedIndex,
    };
  }

  // Keyboard interactions
  @HostListener('keydown', ['$event'])
  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      if (this.isDropdownOpen) {
        clearTimeout(this.closeTimeout);
        this.isDropdownOpen = false;
        if (!this.isMultiselect) {
          this.selectedValue = this._defaultSelected + '';
          this.searchQuery = this._defaultSelected + '';
        } else {
          this.selectedValue = '';
          this.searchQuery = '';
        }
      }
      return;
    }

    if (this.isDropdownOpen) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.highlightNextItem();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.highlightPreviousItem();
      } else if (event.key === ' ' || event.key === 'Enter') {
        if (this.highlightedIndex !== null) {
          event.preventDefault();
          const highlightedItem = this.filteredItems[this.highlightedIndex];
          this.onSelectItem(highlightedItem);
        }
      }
    } else if (!this.isDropdownOpen) {
      if (event.key === 'Enter') {
        this.isDropdownOpen = true;
        this.scrollToHighlighted();
      }
    }
    this.elementRef.nativeElement.focus();
  }

  // Highlight next item in the dropdown
  highlightNextItem() {
    if (this.highlightedIndex === null) {
      this.highlightedIndex = 0;
    } else if (this.highlightedIndex < this.filteredItems.length - 1) {
      this.highlightedIndex++;
    } else {
      // Loop to the top
      this.highlightedIndex = 0;
    }
    this.scrollToHighlighted();
    this.elementRef.nativeElement.focus();
  }

  // Highlight previous item in the dropdown
  highlightPreviousItem() {
    if (!this.highlightedIndex) {
      this.highlightedIndex = this.filteredItems.length - 1;
    } else {
      this.highlightedIndex--;
    }
    this.scrollToHighlighted();
    this.elementRef.nativeElement.focus();
  }

  handlePropagation(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    clearTimeout(this.closeTimeout);
    this.elementRef.nativeElement.focus();
  }

  // Ensure highlighted item is visible in the scrollable list
  scrollToHighlighted() {
    setTimeout(() => {
      const dropdown = this.elementRef.nativeElement.querySelector('.dropdown');
      const highlightedItem = dropdown.querySelector('.highlighted');
      if (highlightedItem) {
        // console.log(
        //   highlightedItem.offsetTop,
        //   dropdown.offsetTop,
        //   dropdown.scrollTop
        // );
        dropdown.scrollTop =
          highlightedItem.offsetTop - dropdown.offsetTop - 100;
      }
    }, 20);
  }

  // Ensure highlighted item is visible in the scrollable list
  scrollToSelected() {
    setTimeout(() => {
      if (this.isDropdownOpen) {
        const dropdown =
          this.elementRef.nativeElement.querySelector('.dropdown');
        const selectedItem = dropdown.querySelector('.selected');
        if (selectedItem) {
          dropdown.scrollTop = selectedItem.offsetTop - dropdown.offsetTop;
        }
      }
    }, 20);
  }

  // Handle blur and close dropdown after a timeout
  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      clearTimeout(this.closeTimeout);
      this.isDropdownOpen = false;
    }
  }

  // Reset close timeout if user hovers back
  @HostListener('mouseleave')
  onMouseLeave() {
    this.mouseLeft = true;
    this.startCloseTimeout();
  }
}
